/**
 * RetentionCurveWidget - Кривая удержания
 * ==========================================
 *
 * % когорты (по дате регистрации), ещё активной на day-й день после
 * регистрации, взвешенный по размеру когорт — не среднее по когортам.
 * Линия на каждую платформу + пунктирный агрегат «Все платформы».
 */

import React, { useMemo } from 'react';
import { BaseWidget, useBaseWidget } from './BaseWidget';
import { useWidgetData } from '../../hooks/useWidgetData';
import { MetricsService } from '../../services/metricsService';
import { RetentionGeoFilter } from './RetentionGeoFilter';
import { useRetentionGeo } from '../../hooks/useRetentionGeo';
import {
  ALL_PLATFORMS,
  AGGREGATE_DASH,
  getPlatformColor,
  getPlatformLabel,
  sortPlatforms
} from '../../config/platforms';
import { retention as retentionSpecs } from '@ggstat/chart-specs';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import { Tooltip } from '../charts/OrderedTooltip';
import { ResponsiveChart as ResponsiveContainer } from '../ui/responsive-chart';

const MAX_DAYS = retentionSpecs.curve.maxDays;

export function RetentionCurveWidget() {
  const { geos, geoKey, toggleGeo, clearGeos } = useRetentionGeo();

  const widgetData = useWidgetData(
    async (filters, options) => {
      return MetricsService.getRetentionCurve({ ...filters, geo: geos }, MAX_DAYS, options);
    },
    [geoKey]
  );

  const widgetProps = useBaseWidget(widgetData);
  const { data: points } = widgetProps;

  // Плоские точки { platform, day, retention } → широкий формат для recharts:
  // { day, all, android, ios }
  const { chartData, platforms } = useMemo(() => {
    const list = Array.isArray(points) ? points : [];
    const byDay = new Map();
    const seen = new Set();

    list.forEach((point) => {
      const day = Number(point.day);
      const platform = point.platform || ALL_PLATFORMS;
      seen.add(platform);

      const row = byDay.get(day) || { day };
      row[platform] = point.retention;
      byDay.set(day, row);
    });

    // При фильтре по одной платформе агрегат «Все платформы» — та же линия,
    // рисовать её вторым слоем незачем
    const realPlatforms = [...seen].filter((platform) => platform !== ALL_PLATFORMS);
    const visible = realPlatforms.length > 1 ? [...seen] : realPlatforms;

    return {
      chartData: [...byDay.values()].sort((a, b) => a.day - b.day),
      platforms: sortPlatforms(visible)
    };
  }, [points]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    return (
      <div className="bg-background border rounded-lg shadow-lg p-3">
        <p className="font-medium mb-2">День {label}</p>
        {payload.map((entry) => (
          <p key={entry.dataKey} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {entry.value}%
          </p>
        ))}
      </div>
    );
  };

  return (
    <BaseWidget
      {...widgetProps}
      title="Кривая удержания"
      subtitle="% когорты, ещё активной на N-й день после регистрации, по платформам"
      emptyMessage="Нет данных за выбранный период"
      skeletonRows={8}
      headerAction={
        <RetentionGeoFilter geos={geos} onToggleGeo={toggleGeo} onClearGeos={clearGeos} />
      }
    >
      <ResponsiveContainer width="100%" height={420}>
        <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis
            dataKey="day"
            type="number"
            domain={[0, MAX_DAYS]}
            allowDecimals={false}
            tick={{ fill: 'currentColor' }}
            className="text-xs"
            label={{ value: 'День после регистрации', position: 'insideBottom', offset: -2 }}
          />
          <YAxis
            domain={[0, 100]}
            tick={{ fill: 'currentColor' }}
            className="text-xs"
            allowDecimals={false}
            label={{ value: '% удержания', angle: -90, position: 'insideLeft' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          {platforms.map((platform) => (
            <Line
              key={platform}
              type="monotone"
              dataKey={platform}
              name={getPlatformLabel(platform)}
              stroke={getPlatformColor(platform)}
              strokeWidth={2}
              strokeDasharray={platform === ALL_PLATFORMS ? AGGREGATE_DASH : undefined}
              dot={false}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </BaseWidget>
  );
}

export default RetentionCurveWidget;
